import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../../../hooks/api";
import { useGlobal } from "../../../../hooks/global";
import { contractsAttributes } from "../../../../interfaces";
import { background, borderColors, primary, secondary } from "../../../../theme";
import { correctDate } from "../../../../services/generalServices";
import { getAuthor, getFranchise, getStatus } from "../../../../utils/columnsTables";
import { contract_status } from "../../../../utils/contract_status";
import { ContractExpiration, contractExpirationLink, expirationOptions } from "../../../../utils/contractExpiration";
import "./style.css";
import { DocumentSlider } from "./DocumentSlider";
import { LabelNoData } from "../LastProposals/style";
import { featureGuideByCode } from "../../../../utils/featureGuides";
import { FiCheckCircle, FiClock } from "react-icons/fi";

interface Result {
  rows: contractsAttributes[];
  count: number;
  loading: boolean;
  error?: string;
  unavailable?: boolean;
}

interface ExpiringDocumentsProps {
  franchiseId?: string;
  refreshAt: Date | number;
  hidden?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onRetry?: () => void;
}

const emptyResult = (): Result => ({ rows: [], count: 0, loading: true });

const scrollExpandedPanelIntoView = (value: ContractExpiration) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const target = document.getElementById(`guide-expiration-${value}`);
      const panel = target?.closest<HTMLElement>(".expiration-panel");
      if (!panel) return;

      const header = document.getElementById("header-content");
      const headerBottom = header?.getBoundingClientRect().bottom || 0;
      const visibleTop = header ? Math.max(0, headerBottom) + 12 : 0;
      const visibleBottom = window.visualViewport?.height || window.innerHeight;
      const panelRect = panel.getBoundingClientRect();
      const panelFitsVisibleArea = panelRect.top >= visibleTop && panelRect.bottom <= visibleBottom;

      if (!panelFitsVisibleArea) {
        window.scrollTo({
          top: Math.max(0, window.scrollY + panelRect.top - visibleTop),
          behavior: "smooth",
        });
      }
    });
  });
};

// Witnesses are stored separately, but also participate in the signatures.
export const documentSignatories = (contract: contractsAttributes) => {
  let witnesses = contract.witnesses;
  if (typeof witnesses === "string") {
    try { witnesses = JSON.parse(witnesses); } catch { witnesses = []; }
  }
  return [...(contract.signatories || []), ...(Array.isArray(witnesses) ? witnesses : [])];
};

const ResponsiveExpirationStatus: React.FC<{ status: string; theme: any }> = ({ status, theme }) => {
  const definition = contract_status[status] || { text: "Status indisponível", color: "#777" };
  const compactIcon = status === "signed"
    ? <FiCheckCircle size={18} aria-hidden="true" />
    : status === "pending" ? <FiClock size={18} aria-hidden="true" /> : null;
  return <>
    <div className={compactIcon ? "expiration-status-label has-compact" : "expiration-status-label"}>
      {getStatus({ ...definition, theme })}
    </div>
    {compactIcon && <span className="expiration-status-compact" role="img" aria-label={definition.text}
      title={definition.text} style={{ color: definition.color }}>{compactIcon}</span>}
  </>;
};

export const ExpiringDocuments: React.FC<ExpiringDocumentsProps> = ({
  franchiseId, refreshAt, hidden, onLoadingChange, onRetry,
}) => {
  const { api, user } = useApi();
  const { theme, franchise, activeFeatureGuide } = useGlobal();
  const apiRef = useRef(api);
  apiRef.current = api;
  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;
  const previousScope = useRef("");
  const dashboardLoadPending = useRef(false);
  const refreshTrigger = refreshAt instanceof Date ? refreshAt.getTime() : refreshAt;
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (activeFeatureGuide?.code === 'electronic_documents_expiration') {
      const guideStep = featureGuideByCode(activeFeatureGuide.code)?.steps[activeFeatureGuide.step];
      if (guideStep?.targetId === 'guide-expiration-upcoming') setExpanded(false);
      if (guideStep?.expandDocuments) setExpanded(true);
    }
  }, [activeFeatureGuide]);
  const [results, setResults] = useState<Record<ContractExpiration, Result>>({
    upcoming: emptyResult(), recently_expired: emptyResult(),
  });
  const scope = user.type === "root" ? franchiseId ?? "" : franchise?.id;
  const scopeKey = `${user.id}:${user.type}:${scope || ""}`;
  const [loadedScope, setLoadedScope] = useState("");

  useEffect(() => {
    let active = true;
    let generation = 0;
    const load = (blockDashboard = false) => {
      const request = ++generation;
      if (blockDashboard) {
        dashboardLoadPending.current = true;
          onLoadingChangeRef.current?.(true);
      }
      setLoadedScope(scopeKey);
      // Keep the current slide visible while the dashboard refreshes this scope.
      if (blockDashboard) setResults({ upcoming: emptyResult(), recently_expired: emptyResult() });
      let remainingRequests = expirationOptions.length;
      const finishRequest = () => {
        remainingRequests -= 1;
        if (active && request === generation && remainingRequests === 0 && dashboardLoadPending.current) {
          dashboardLoadPending.current = false;
          onLoadingChangeRef.current?.(false);
        }
      };
      expirationOptions.forEach(async ({ value }) => {
        const query = new URLSearchParams({ expiration: value, page: "1", limit: "10" });
        if (scope !== undefined) query.set("franchise_id", scope);
        try {
          // Optional dashboard reads must not redirect the entire page to billing.
          // Authorization remains enforced by the existing API middleware.
          const response = await apiRef.current.get(`/generic_searches/contracts?${query}`, {
            validateStatus: status => (status >= 200 && status < 300) || [401, 402, 403].includes(status),
          });
          if (!active || request !== generation) return;
          const result: Result = response.status >= 400
            ? {
              rows: [], count: 0, loading: false, unavailable: true,
              error: "Documentos indisponíveis para o acesso atual."
            }
            : { rows: response.data.rows, count: Number(response.data.count), loading: false };
          setResults(previous => ({ ...previous, [value]: result }));
        } catch {
          if (!active || request !== generation) return;
          setResults(previous => ({
            ...previous, [value]: {
              ...previous[value], loading: false, error: "Não foi possível carregar os documentos.",
            }
          }));
        } finally {
          finishRequest();
        }
      });
    };
    const scopeChanged = previousScope.current !== scopeKey;
    previousScope.current = scopeKey;
    load(scopeChanged);
    return () => { active = false; };
  }, [scopeKey, scope, refreshTrigger]);

  return (
    <div className="expiration-panels" hidden={hidden} style={{
      "--expiration-surface": primary[theme], "--expiration-background": background[theme],
      "--expiration-border": borderColors[theme], "--expiration-accent": secondary[theme],
    } as React.CSSProperties}>
      {expirationOptions.map(({ value, label }) => {
        const result = loadedScope === scopeKey ? results[value] : emptyResult();
        const isExpanded = expanded;
        const toggleExpanded = () => {
          const shouldExpand = !expanded;
          setExpanded(shouldExpand);
          if (shouldExpand) scrollExpandedPanelIntoView(value);
          else window.scrollTo({ top: 0, behavior: "smooth" });
        };
        return (
          <section className="expiration-panel" key={value} aria-label={`Documentos eletrônicos: ${label}`}>
            <header id={`guide-expiration-${value}`} className={`expiration-panel-header${isExpanded ? " is-expanded" : ""}`}
              role="button" tabIndex={0} aria-expanded={isExpanded} aria-controls={`expiration-content-${value}`}
              onClick={toggleExpanded}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleExpanded();
                }
              }}>
              <div>
                <span className="expiration-eyebrow">Documentos eletrônicos</span>
                <h2>
                  <span className="expiration-panel-toggle">
                    {label}<span aria-hidden="true">{isExpanded ? "▴" : "▾"}</span>
                  </span>
                </h2>
                <p>{value === "upcoming" ? "Aguardando assinatura · prazo mais próximo primeiro" : "Expiração nos últimos 7 dias · atualização mais recente primeiro"}</p>
              </div>
              {!result.loading && !result.error && <span className="expiration-count" aria-label={`${result.count} documentos`}>{result.count}</span>}
            </header>
            <div id={`expiration-content-${value}`} hidden={!isExpanded}>
              <div className="expiration-document-list" aria-busy={result.loading}>
                {result.loading && <p className="expiration-feedback" role="status">Carregando documentos…</p>}
                {result.error && <div className="expiration-feedback" role="status">
                  <p>{result.error}</p>
                  {!result.unavailable && onRetry && <button type="button" onClick={onRetry}>Tentar novamente</button>}
                </div>}
                {!result.loading && !result.error && !result.rows.length &&
                  <LabelNoData className="expiration-empty" role="status">Sem dados</LabelNoData>}
                <DocumentSlider key={`${scopeKey}:${value}`} documents={result.rows} label={label} renderDocument={contract => {
                  const signatories = documentSignatories(contract);
                  const bond = contract.property_id ? `Imóvel #${contract.property?.friendly_id || "—"}`
                    : contract.proposal_id ? `Proposta #${contract.proposal?.friendly_id || "—"}` : "Documento avulso";
                  return <article className="expiration-document" key={contract.id}>
                    <div className="expiration-document-title">
                      <Link title={contract.name} to={contractExpirationLink(value, scope, contract.id)}>#{contract.friendly_id || "—"} · {contract.name}</Link>
                    <div className="expiration-status"><ResponsiveExpirationStatus status={contract.status} theme={theme} /></div>
                    </div>
                    <div className="expiration-deadline"><span>{value === "upcoming" ? "Expira em" : "Expirou em"}</span> <strong>{correctDate(contract.expiries_in)}</strong></div>
                    <div className="expiration-meta"><span>{bond}</span><span className="expiration-author">Autor: {getAuthor({ person: contract, theme })}</span></div>
                    {user.type === "root" && <div className="expiration-meta">Imobiliária: {getFranchise({ person: contract, theme })}</div>}
                    {value === "recently_expired" && <div className="expiration-updated">Atualizado em {correctDate(contract.updatedAt)}</div>}
                    <div className="expiration-signatories">
                      <div>Signatários</div>
                      <ul>{signatories.map((person, index) => <li key={`${person.id || person.email}-${index}`}>
                        <span>{person.name?.toUpperCase() || person.email || "Signatário"}</span>
                      <ResponsiveExpirationStatus status={person.status} theme={theme} />
                      </li>)}</ul>
                      {!signatories.length && <p>Nenhum signatário cadastrado.</p>}
                    </div>
                  </article>;
                }} />
              </div>
              {!result.unavailable && <footer className="expiration-panel-footer">
                <span>{!result.loading && !result.error ? `Exibindo ${result.rows.length} de ${result.count}` : "Até 10 documentos"}</span>
                <Link id={`guide-expiration-full-${value}`} to={contractExpirationLink(value, scope)}>Ver visão completa →</Link>
              </footer>}
            </div>
          </section>
        );
      })}
    </div>
  );
};
